/**
 * AppointmentAuthorizationField.js
 * Bloco "Guia" do drawer de novo agendamento por convênio, logo abaixo de
 * "Pedido médico" e no mesmo padrão: escolhe uma guia ativa da ficha da
 * operadora da carteirinha escolhida (a única utilizável na data já vem
 * escolhida) ou registra uma nova pelo mesmo formulário da ficha, que fica
 * selecionada ao salvar. Quem vincula a guia ao agendamento é o drawer, depois
 * de criar (linkRequestToAppointments com o tipo 'authorization'). Os documentos
 * e as operadoras vêm do drawer (usePatientInsuranceDocuments), buscados uma vez
 * para este bloco e o do pedido.
 *
 * O asterisco só vem quando a operadora exige guia. Nunca bloqueia: sem guia o
 * agendamento salva e o aviso de "Convênios a conferir" continua valendo.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import AuthorizationFormPopup from '../../../../patient/areas/insurance/documents/AuthorizationFormPopup';
import { authorizationStatusShortLabel, formatDate } from '../../../../patient/areas/insurance/documents/documentFormat';
import { toDayKey } from '../../../../../components/day-rooms/dayRooms';
import usePatientInsurances from './usePatientInsurances';
import drawerStyles from './AppointmentDrawer.module.css';
import styles from './CoveragePicker.module.css';

const dateOnly = (value) => (value ? String(value).slice(0, 10) : '');

const isActive = (item) => item.active !== 0 && item.active !== false && item.active !== '0';

const isClosedStatus = (status) => status === 'denied' || status === 'cancelled';

/** Guia vale de valid_from até valid_until; sem data, sem limite daquele lado. */
export const isAuthorizationValidOn = (authorization, dayKey) => {
  if (!dayKey) return true;
  const from = dateOnly(authorization?.valid_from);
  const until = dateOnly(authorization?.valid_until);
  return (!from || from <= dayKey) && (!until || until >= dayKey);
};

/** Datas do agendamento cobertas pela guia (recorrência). */
export const isDateCoveredByAuthorization = (authorization, date) => isAuthorizationValidOn(authorization, dateOnly(date));

// Autorizadas e válidas primeiro, depois aguardando e válidas, por fim o resto
const rank = (authorization, dayKey) => {
  if (isClosedStatus(authorization.status) || !isAuthorizationValidOn(authorization, dayKey)) return 2;
  return authorization.status === 'authorized' ? 0 : 1;
};

function optionLabel(authorization, dayKey) {
  const parts = [
    authorization.reference_number ? `Guia ${authorization.reference_number}` : 'Guia sem número',
    authorizationStatusShortLabel(authorization.status).toLowerCase(),
  ];
  const from = dateOnly(authorization.valid_from);
  const until = dateOnly(authorization.valid_until);
  if (until && dayKey && until < dayKey) parts.push(`venceu em ${formatDate(until)}`);
  else if (from && dayKey && from > dayKey) parts.push(`vale a partir de ${formatDate(from)}`);
  else parts.push(until ? `vence ${formatDate(until)}` : 'sem vencimento');
  return parts.join(' - ');
}

/**
 * @param {Object} props
 * @param {number|string} props.patientId
 * @param {string|null} props.providerId - operadora da carteirinha escolhida
 * @param {number|string|null} props.cardId - carteirinha escolhida
 * @param {Array} props.providers - operadoras da empresa (CompanyConfig.InsuranceProviders)
 * @param {Object} props.insuranceDocuments - retorno de usePatientInsuranceDocuments
 * @param {Date} props.appointmentDate - data do atendimento (a 1ª, na recorrência)
 * @param {Object|null} props.value - guia selecionada ({ id, reference_number, status, valid_until, ... })
 * @param {Function} props.onChange - (guia | null) => void
 * @param {boolean} [props.isRecurring] - texto de ajuda da recorrência
 * @param {boolean} [props.disabled]
 */
export default function AppointmentAuthorizationField({
  patientId, providerId, cardId, providers, insuranceDocuments, appointmentDate, value, onChange,
  isRecurring = false, disabled = false,
}) {
  const { SetInsuranceAuthorization } = DataProviderInstance();
  const { cards } = usePatientInsurances(patientId, patientId != null);
  const { documents, providerRules, reload, addDocument } = insuranceDocuments;
  const [formOpen, setFormOpen] = useState(false);
  const dayKey = appointmentDate ? toDayKey(appointmentDate) : '';

  // Operadoras que exigem guia (o CompanyConfig não traz a exigência)
  const requiredProviders = useMemo(
    () => providerRules.filter((p) => Number(p.requires_authorization) === 1).map((p) => String(p.id)),
    [providerRules],
  );

  // Guias ativas da operadora da carteirinha, na ordem de preferência para a data
  const authorizations = useMemo(() => {
    if (!documents) return null;
    return documents.authorizations
      .filter((a) => isActive(a) && providerId != null && String(a.provider_id) === String(providerId))
      .sort((a, b) => rank(a, dayKey) - rank(b, dayKey) || Number(b.id) - Number(a.id));
  }, [documents, providerId, dayKey]);

  // Trocou a operadora: a guia de outra operadora sai; na primeira carga de cada
  // pessoa + operadora, a única utilizável (autorizada antes) já vem escolhida
  const preselectedFor = useRef(null);
  useEffect(() => {
    if (value && providerId != null && value.provider_id != null && String(value.provider_id) !== String(providerId)) {
      onChange?.(null);
      return;
    }
    if (!authorizations || providerId == null) return;
    const key = `${patientId}:${providerId}`;
    if (preselectedFor.current === key) return;
    preselectedFor.current = key;
    if (value) return;
    const authorized = authorizations.filter((a) => rank(a, dayKey) === 0);
    const usable = authorizations.filter((a) => rank(a, dayKey) < 2);
    if (authorized.length === 1) onChange?.(authorized[0]);
    else if (authorized.length === 0 && usable.length === 1) onChange?.(usable[0]);
  }, [authorizations, providerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const required = providerId != null && requiredProviders.includes(String(providerId));
  const selected = useMemo(
    () => (value ? (authorizations || []).find((a) => String(a.id) === String(value.id)) || value : null),
    [authorizations, value],
  );

  const handleSelect = (id) => {
    onChange?.(id ? (authorizations || []).find((a) => String(a.id) === String(id)) || null : null);
  };

  // Carteirinha recém-cadastrada no drawer ainda não está na lista do hook
  const insurances = useMemo(() => {
    if (cardId == null || cards.some((c) => String(c.id) === String(cardId))) return cards;
    return [{ id: cardId, provider_id: providerId, active: 1 }, ...cards];
  }, [cards, cardId, providerId]);

  // Grava como a ficha e devolve { ok, id } ao formulário; a guia nova fica escolhida
  const saveAuthorization = (values) => new Promise((resolve) => {
    SetInsuranceAuthorization({ patientId, ...values }, (result) => {
      const id = result?.status === 'SUCCESS' ? result.data?.id : null;
      if (!id) {
        resolve({ ok: false, error: result?.error || result?.message || 'Não foi possível salvar a guia. Tente de novo.' });
        return;
      }
      const created = { id, active: 1, ...values };
      addDocument('authorization', created);
      if (String(values.provider_id) === String(providerId)) onChange?.(created);
      // A recarga (contadores e "vencido" do backend) sai no onSaved, depois dos anexos
      resolve({ ok: true, id });
    });
  });

  const hasAuthorizations = (authorizations || []).length > 0;

  let status = null;
  if (selected) {
    const until = dateOnly(selected.valid_until);
    const from = dateOnly(selected.valid_from);
    let warning = null;
    let badge = null;
    if (isClosedStatus(selected.status)) {
      badge = authorizationStatusShortLabel(selected.status);
      warning = `Esta guia está ${authorizationStatusShortLabel(selected.status).toLowerCase()}. Registre uma guia nova.`;
    } else if (until && dayKey && until < dayKey) {
      badge = 'Vencida';
      warning = `Venceu em ${formatDate(until)}, antes da data deste atendimento. Registre uma guia nova.`;
    } else if (from && dayKey && from > dayKey) {
      badge = 'Ainda não vale';
      warning = `Vale a partir de ${formatDate(from)}, depois da data deste atendimento.`;
    }
    status = warning ? (
      <div className={styles.warning}>
        <span className={styles.warningBadge}>{badge}</span>
        <span>{warning}</span>
      </div>
    ) : (
      <span className={styles.hint}>
        {selected.status === 'authorized' ? 'Autorizada' : 'Aguardando autorização'}
        {'. '}
        {until ? `Vence em ${formatDate(until)}` : 'Sem vencimento'}
        {isRecurring && until ? '. Fica nas datas até o vencimento.' : ''}
      </span>
    );
  } else if (authorizations !== null) {
    status = (
      <span className={styles.hint}>
        {hasAuthorizations ? '' : 'Nenhuma guia desta operadora na ficha. '}
        {required
          ? 'A operadora exige guia; sem ela o agendamento é salvo e fica em Convênios a conferir.'
          : 'Opcional para esta operadora.'}
      </span>
    );
  }

  return (
    <div className={drawerStyles.formSection}>
      <div className={styles.headerRow}>
        <label
          className={`${drawerStyles.fieldLabel} ${required ? drawerStyles.fieldLabelRequired : ''}`}
          htmlFor="appointment-authorization"
        >
          Guia
        </label>
        <button type="button" className={styles.linkBtn} onClick={() => setFormOpen(true)} disabled={disabled}>
          Registrar guia
        </button>
      </div>
      {authorizations === null ? (
        <span className={styles.loading}>Carregando guias...</span>
      ) : hasAuthorizations && (
        <select
          id="appointment-authorization"
          className={drawerStyles.fieldSelect}
          value={selected ? String(selected.id) : ''}
          onChange={(e) => handleSelect(e.target.value)}
          disabled={disabled}
        >
          <option value="">Sem guia</option>
          {authorizations.map((a) => (
            <option key={a.id} value={String(a.id)}>{optionLabel(a, dayKey)}</option>
          ))}
        </select>
      )}
      {status}

      {formOpen && (
        <AuthorizationFormPopup
          patientId={patientId}
          insurances={insurances}
          providers={providers}
          requests={documents?.requests || []}
          defaultCardId={cardId}
          onSave={saveAuthorization}
          onSaved={() => reload()}
          onClose={() => setFormOpen(false)}
        />
      )}
    </div>
  );
}
